
import React, { useEffect, useContext } from 'react'
import {Grid, TextField} from '@material-ui/core'
import AppContext from '../../../../contexts/App/App.context'
import PriorAuthorizationContext from "../../../../contexts/PriorAuthorization/PriorAuthorization.context";

import config, {defaultInputProps, 
                defaultLabelWrapper, 
                defaultLabelProps,
                defaultHelperTextProps,
                defaultHeaderWrapper,
                defaultFieldDims,
                fieldGroups} from '../formConfig'
import RequiredInfo from '../../../Common/RequiredInfo'
import DatePickerInput from '../../CreatePa/InputTypes/DatePickerInput'
import HelpIcon from '../../../Common/HelpIcon'
import useStyles from './styles'
import {isEmpty} from '../../../../shared/utils'

export default function UniversalSearch(props){

    const { context, operation, setFormError, ...wrapperProps} = props
    const {fields, setFields, authNumber, setAuthNumber, resetFields} = useContext(PriorAuthorizationContext)
    const classes = useStyles()
    const appContext = useContext(AppContext)

    const onChange = e => onUpdate({e, updateType: 'onChange'})
    
    // each field uses onblur, checkboxes call onblur when clicked
    const onBlur = e => onUpdate({e, updateType: 'onBlur'})

    // update the context for blur or change
    const onUpdate = params => {
        const {e: {target: {name, value}}, updateType} = params;
        const isDirty = updateType === 'onBlur' ? true : fields[name].isDirty
        const cb = config[name][updateType] ? () => config[name][updateType](value, context) : null
        if (name === undefined || value === undefined) {
            return;
        }

        setFields({[name]: {value, isDirty}}, cb);

        const fieldsToReset =   fields.beneficiaryID.value 
                                    ? filterDirtyFields(['beneficiaryLastName', 'beneficiaryDOB'])
                                    : (fields.beneficiaryLastName.value || fields.beneficiaryDOB.value) 
                                        ? filterDirtyFields(['beneficiaryID'])
                                        : []
        if(fieldsToReset.length > 0){
            resetFields(fieldsToReset)
        }
    }

    const filterDirtyFields = _fields => {
        const dirtyFields = []
        _fields.forEach( _field => {
            if(fields[_field].isDirty){
                dirtyFields.push(_field)
            }
        })
        return dirtyFields
    }

    const isAuthValid = (authNumber) => /^[a-zA-Z0-9]+$/.test(authNumber)

    const handleAuthNumber = e => {
        const authNumber = e.target.value
        setAuthNumber(authNumber)
        const isValid = isAuthValid(authNumber)
        if(isValid){
            setFormError(false)
        }
        else{
            setFormError(true)
        }
        const fs = filterDirtyFields(['beneficiaryID', 
            'beneficiaryLastName', 
            'beneficiaryDOB', 
            'requestBeginDate', 
            'requestEndDate'])

        if(fs.length > 0){
            resetFields(fs)
        }
    }

    const onDateChange = ({target: {value, isDirty=true}}, field) => {
        setFields({[field]: {value, isDirty}});
        if(config[field]['onChange']){
            config[field]['onChange'](value, context)
        }
    }

    const hasDateMinMaxError = (field) => {
        const date = window.moment(fields[field].value, 'MM/DD/YYYY')
        const min = config[field].minDate ? config[field].minDate(context) : null
        const max = config[field].maxDate ? config[field].maxDate(context) : null
        const isAfter = min && window.moment(min).isValid() ? date.isSameOrAfter(min, 'day') : true
        const isBefore =  max && window.moment(max).isValid() ? date.isSameOrBefore(max, 'day') : true

        return !isBefore || !isAfter
    }

    const hasFormError =  requiredFields => {
        const hasErrors = requiredFields.some( f => {
            if(config[f].type === 'authNumber'){
                return isEmpty(authNumber)
            }
            else{
                const hasValidationErrors = fields[f].errors.length > 0
                if(config[f].type === 'date'){
                    return hasDateMinMaxError(f) || hasValidationErrors || isEmpty(fields[f].value)
                }
                return hasValidationErrors || isEmpty(fields[f].value)
            }
        })
        return hasErrors
    }

    // Evaluate form
    useEffect(() => {
        const formFields = fieldGroups.flat().filter(f => {
            const disabled = config[f].disabled ? config[f].disabled(context) : false
            const visible  = config[f].visible ? config[f].visible(context) : true
            return ( config[f].required && !disabled && visible )
        })
        const hasError = hasFormError(formFields)
        if(hasError){
            setFormError(true)
        }
        else{
            setFormError(false)
        }
    // eslint-disable-next-line
    }, [context.fields])

    // Initialize default values
    useEffect(() =>{
        const defaultValueFields = fieldGroups.flat().filter(f => config[f].defaultValue)
        const defaultState = {}
        defaultValueFields.forEach( f => {
            defaultState[f] = config[f].defaultValue
        })
        setFields(defaultState)
    // eslint-disable-next-line
    }, [])

    return(
        <Grid 
            id='MEDCAL_RX_UNIVERSAL_SEARCH' 
            {...wrapperProps} 
            container 
            spacing={1}
        >
            { fieldGroups.map( (group, index) => {
                const isHeaderGroup = group.every( f => config[f].type === 'header')
                const isGrouped = group.length > 0 && !isHeaderGroup
                return (
                    <Grid key={index}
                        container spacing={2} 
                        className={ isGrouped ? classes.fieldGroup : null}
                        item xs={12}
                    >
                        {group.map( (field, index) => {
                            
                            const prefilledVal   = config[field].value ? config[field].value(appContext) : null
                            const tooltip = config[field].tooltip ? config[field].tooltip(operation) : null
                            const tooltipStyle = config[field].tooltipStyle
                            const fieldDims = config[field].fieldDims
                            const maxDate = config[field].maxDate
                            const minDate = config[field].minDate
                            const isHeaderType = config[field].type === 'header'
                            const disabled = config[field].disabled ? config[field].disabled(context) : false
                            const visible = config[field].visible ? config[field].visible(context) : true

                            const canEvaluateField = fields[field] && fields[field].isDirty && !disabled
                            const hasError     = canEvaluateField && fields[field].errors.length > 0
                            const hasDateError = canEvaluateField && hasDateMinMaxError(field)
                            const helperText = (config[field].helperText || ( hasError && fields[field].errors[0]) )
                            
                            const customInputProps = config[field].input ? config[field].input.inputProps : {}
                            const inputProps = {...defaultInputProps.inputProps, ...customInputProps}

                            return visible && (
                                <Grid key={index} className={classes[field]}
                                    container spacing={0}
                                    item {...defaultFieldDims(operation)} {...fieldDims}
                                >
                                    <Grid 
                                        style={ !isHeaderGroup ? {...defaultLabelWrapper} : {...defaultHeaderWrapper} }
                                        container 
                                        item xs={12} sm={ isHeaderType ? 12 : 6} 
                                    >
                                        <label {...defaultLabelProps} {...config[field].labelProps} htmlFor={`${field}_input`}>
                                            { config[field].required && !disabled && <RequiredInfo mode="asterisk"/> }
                                            {config[field].label}
                                        </label>
                                        {tooltip && <HelpIcon tooltip={tooltip} tooltipStyle={tooltipStyle}/>}
                                    </Grid>
                                    { !isHeaderType &&
                                    <Grid 
                                        item xs={12} sm={ isGrouped ? 12 : 6 } md={ 6 } 
                                        style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap'}}
                                    >
                                        { config[field].type === 'authNumber' &&
                                            <TextField
                                                id={`${field}_input`}
                                                name={field}
                                                {...defaultInputProps}
                                                {...config[field].input}
                                                inputProps={ inputProps }
                                                value={ authNumber || '' }
                                                onChange={handleAuthNumber}
                                                error={!isAuthValid(authNumber) && authNumber.length > 0}
                                                helperText={!isAuthValid(authNumber) && authNumber.length > 0 && 'Invalid data'}
                                                FormHelperTextProps={{style:{color: "#a32d24"}}}
                                                disabled={disabled ? true : false}
                                            />
                                        }
                                        { config[field].type === 'prefilled' &&
                                            <span>{prefilledVal}</span>
                                        }
                                        { config[field].type === 'text' &&
                                            <TextField
                                                id={`${field}_input`}
                                                name={field}
                                                {...defaultInputProps}
                                                {...config[field].input}
                                                inputProps={ inputProps }
                                                value={fields[field].value}
                                                onBlur={onBlur}
                                                onChange={onChange}
                                                error={ hasError }
                                                helperText={ hasError && helperText }
                                                FormHelperTextProps={{style:{color: "#a32d24"}}}
                                                disabled={ disabled ? true : false }
                                            />
                                        }
                                        { config[field].type === 'date' &&
                                            <DatePickerInput
                                                id={`${field}_input`}
                                                style={{...defaultInputProps.style, ...config[field].input.style}}
                                                name={field}
                                                onChange={e => onDateChange(e, field)}
                                                error={hasDateError || hasError}
                                                helperText={ (hasError && fields[field].errors[0]) || (hasDateError && helperText) }
                                                FormHelperTextProps={{style: {...defaultHelperTextProps, color: "#a32d24"}}}
                                                disabled={ disabled ? true : false }
                                                maxDate={maxDate ? maxDate(context) : undefined}
                                                minDate={minDate ? minDate(context) : undefined}
                                                inputProps={{autocomplete: 'off'}}
                                                value={fields[field].value}
                                            />
                                        }
                                    </Grid>
                                    }
                                </Grid>
                            )
                        })}
                    </Grid>
                )
            })}
        </Grid>
    )
}